import React, { useEffect, useState } from "react";
import Image from "next/image";
import { m } from "framer-motion";
import PropTypes from "prop-types";

const DEFAULT_TEXT = "Inspired by artistry, objects, designs and the future";
const DEFAULT_IMAGES = {
    left: "/images/chair.svg",
    right: "/images/chair2.svg",
};

const imageVariants = {
    hidden: (isLeft) => ({ opacity: 0, x: isLeft ? 80 : -80, rotate: 0 }),
    visible: (isLeft) => ({ opacity: 1, x: 0, rotate: isLeft ? -18 : 18 }),
};

const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
};

export default function IconRow({ aboutDynamicImage }) {
    const [animateKey, setAnimateKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimateKey((prev) => prev + 1); // Force re-render of animation
        }, 3000); // every 1 second

        return () => clearInterval(interval);
    }, []);

    const text = aboutDynamicImage?.text || DEFAULT_TEXT;

    const leftImage = {
        url: aboutDynamicImage?.leftImage?.data?.attributes?.url || DEFAULT_IMAGES.left,
        alternativeText:
            aboutDynamicImage?.leftImage?.data?.attributes?.alternativeText || "left icon",
    };

    const rightImage = {
        url: aboutDynamicImage?.rightImage?.data?.attributes?.url || DEFAULT_IMAGES.right,
        alternativeText:
            aboutDynamicImage?.rightImage?.data?.attributes?.alternativeText || "right icon",
    };

    return (
        <div className="bg-[#E9E7DE] w-full flex flex-row items-center justify-center md:gap-8 py-20 overflow-hidden">
            {/* Left Image */}
            <m.div
                key={`left-${animateKey}`}
                custom={true}
                initial="hidden"
                animate="visible"
                variants={imageVariants}
                transition={{ duration: 0.8 }}
                className="lg:w-[300px] lg:h-[300px] md:w-[200px] md:h-[200px] w-[100px] h-[100px] relative shrink-0"
            >
                <Image
                    src={leftImage.url}
                    alt={leftImage.alternativeText}
                    sizes="(max-width: 479px) 500px, 500px"
                    fill
                    className="w-full h-full object-contain"
                />
            </m.div>

            {/* Text */}
            <m.h2
                key={`text-${animateKey}`}
                initial="hidden"
                animate="visible"
                variants={textVariants}
                transition={{ duration: 0.8 }}
                className="text-darkGrey font-light font-havletica shrink min-w-[200px] lg:text-[45px] md:text-[30px] text-[20px] uppercase text-center lg:max-w-[800px] z-20"
            >
                {text}
            </m.h2>

            {/* Right Image */}
            <m.div
                key={`right-${animateKey}`}
                custom={false}
                initial="hidden"
                animate="visible"
                variants={imageVariants}
                transition={{ duration: 0.8 }}
                className="lg:w-[300px] lg:h-[300px] md:w-[200px] md:h-[200px] w-[100px] h-[100px] relative shrink-0"
            >
                <Image
                    src={rightImage.url}
                    alt={rightImage.alternativeText}
                    sizes="(max-width: 479px) 500px, 500px"
                    fill
                    className="w-full h-full object-contain"
                />
            </m.div>
        </div>
    );
}

IconRow.propTypes = {
    aboutDynamicImage: PropTypes.shape({
        text: PropTypes.string,
        leftImage: PropTypes.shape({
            data: PropTypes.shape({
                attributes: PropTypes.shape({
                    url: PropTypes.string,
                    alternativeText: PropTypes.string,
                }),
            }),
        }),
        rightImage: PropTypes.shape({
            data: PropTypes.shape({
                attributes: PropTypes.shape({
                    url: PropTypes.string,
                    alternativeText: PropTypes.string,
                }),
            }),
        }),
    }),
};
